import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InboxItem } from '@dtable/shared-types';
import { apiFetch } from '../lib/api-client';
import { useAuthStore } from '../stores/auth-store';

const KEY = ['inbox'] as const;

export function useInbox() {
  return useQuery<InboxItem[]>({
    queryKey: [...KEY, 'list'],
    queryFn: () => apiFetch<InboxItem[]>('/inbox'),
    refetchInterval: 30_000,
  });
}

/**
 * Unread count is polled every 15 seconds so the top-bar badge stays near-live
 * without the auth-header gymnastics EventSource forces. Swap to SSE later if
 * we need sub-second freshness.
 */
export function useInboxUnreadCount() {
  const token = useAuthStore((s) => s.accessToken);
  return useQuery<{ count: number }>({
    queryKey: [...KEY, 'unread-count'],
    queryFn: () => apiFetch<{ count: number }>('/inbox/unread-count'),
    enabled: Boolean(token),
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
}

export function useMarkInboxRead() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiFetch<void>(`/inbox/${id}/read`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useMarkAllInboxRead() {
  const qc = useQueryClient();
  return useMutation<{ updated: number }, Error, void>({
    mutationFn: () =>
      apiFetch<{ updated: number }>('/inbox/read-all', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
